import { useRef, useState, useLayoutEffect } from "react";

/**
 * Scales its children to fit the available space.
 * - Default: fits BOTH width and height (never scrolls) — good for desktop.
 * - scroll=true: fits to WIDTH only and lets the block scroll vertically. This
 *   keeps tap targets large on mobile (where the short landscape height would
 *   otherwise shrink everything). Button styling is unchanged; only the overall
 *   scale differs.
 */
export function FitScreen({ children, maxScale = 1, fill = 1, scroll = false, className = "" }) {
  const outer = useRef(null);
  const inner = useRef(null);
  const [scale, setScale] = useState(maxScale);
  const [innerH, setInnerH] = useState(null);

  useLayoutEffect(() => {
    const fit = () => {
      const o = outer.current;
      const i = inner.current;
      if (!o || !i) return;
      const ih = i.offsetHeight;
      const iw = i.offsetWidth;
      if (!ih || !iw) return;
      let s;
      if (scroll) {
        s = Math.min(maxScale, (o.clientWidth / iw) * fill);
      } else {
        s = Math.min(maxScale, (o.clientHeight / ih) * fill, (o.clientWidth / iw) * fill);
      }
      s = s > 0 ? s : maxScale;
      setScale(s);
      setInnerH(ih * s);
    };
    fit();
    window.addEventListener("resize", fit);
    window.addEventListener("orientationchange", fit);
    let ro;
    if (typeof ResizeObserver !== "undefined" && inner.current) {
      ro = new ResizeObserver(fit);
      ro.observe(inner.current);
    }
    const t = setTimeout(fit, 300); // after fonts/images settle
    return () => {
      window.removeEventListener("resize", fit);
      window.removeEventListener("orientationchange", fit);
      if (ro) ro.disconnect();
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scroll]);

  if (scroll) {
    return (
      <div ref={outer} className={`w-full h-full overflow-y-auto overflow-x-hidden overscroll-contain ${className}`}>
        <div style={{ height: innerH ? `${innerH}px` : undefined, overflow: "hidden" }} className="w-full text-center">
          <div ref={inner} className="inline-block align-top" style={{ transform: `scale(${scale})`, transformOrigin: "top center" }}>
            {children}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={outer} className={`w-full h-full flex items-center justify-center overflow-hidden ${className}`}>
      <div ref={inner} style={{ transform: `scale(${scale})`, transformOrigin: "center center" }}>
        {children}
      </div>
    </div>
  );
}
